import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SignUpButton } from "@clerk/react";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { getGeneratedImage } from "@/lib/safe-assets";

// Resolve the optional background images via the safe loader — attached_assets/
// is gitignored (Replit-only), so a static import would break clean builds (CI).
// Returns undefined when absent; the gradient overlays below stand in.
const ctaDesktop = getGeneratedImage("cta-desktop.webp");
const ctaMobile = getGeneratedImage("cta-mobile.webp");

export function FinalCTA() {
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.15 });

  return (
    <section
      ref={ref}
      id="join"
      className="relative py-32 md:py-40 lg:py-48 scroll-mt-20 overflow-hidden"
    >
      {/* Background image — desktop landscape + mobile portrait for sharpness */}
      <div
        className="absolute inset-0 animate-hero-drift lg:will-change-transform hidden md:block"
        style={{
          backgroundImage: ctaDesktop ? `url(${ctaDesktop})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className="absolute inset-0 animate-hero-drift md:hidden"
        style={{
          backgroundImage: ctaMobile ? `url(${ctaMobile})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center 40%",
        }}
      />

      {/* Multi-layer overlay for cinematic depth */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--brand-coral) / 0.70) 0%, hsl(var(--brand-navy) / 0.85) 50%, hsl(218 65% 8% / 0.90) 100%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, hsl(var(--brand-coral) / 0.08) 0%, transparent 60%)",
        }}
      />

      {/* Grain texture */}
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div
        className={`relative z-10 max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <Heading level={1} as="h2" serif className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-7">
          {t("cta.title")}
        </Heading>

        <p className="text-lg md:text-xl text-primary-foreground/80 mb-12 max-w-2xl mx-auto leading-relaxed">
          {t("cta.subtitle")}
        </p>

        <SignUpButton mode="redirect">
          <Button
            size="lg"
            className="bg-brand-coral hover:bg-brand-coral/90 text-white font-bold text-base md:text-lg px-10 py-7 rounded-full shadow-xl shadow-brand-coral/25 animate-pulse-glow transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl hover:shadow-brand-coral/30"
            data-testid="button-join-now"
          >
            {t("cta.joinNow")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </SignUpButton>

        <p
          className="text-sm text-white/80 mt-8 tracking-wide"
          data-testid="text-cta-trust-line"
        >
          {t("cta.freeToStart")} &bull; {t("cta.noCreditCard")} &bull; {t("cta.startMatching")}
        </p>
      </div>
    </section>
  );
}
